import React, { useState, useEffect, useRef } from 'react';
import { Play, MessageCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderPill from './AnimatedBorderPill';

interface Testimonial {
  id: number;
  videoUrl: string;
  thumbnail: string;
}

interface EditorialVideoTestimonialsProps {
  testimonials: Testimonial[];
  description?: string;
}

const EditorialVideoTestimonials: React.FC<EditorialVideoTestimonialsProps> = ({ 
  testimonials,
  description = "See how businesses like yours are transforming their marketing with Storily.io."
}) => {
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  
  // Theme-aware colors
  const bgColor = theme === 'dark' ? '#111111' : '#FCFCF9';
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const borderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const cardBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  
  // Duplicate testimonials for infinite scroll effect
  const duplicatedTestimonials = [...testimonials, ...testimonials, ...testimonials];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isPaused) return;

    let animationId: number;
    let scrollPosition = scrollContainer.scrollLeft;
    const scrollSpeed = 0.5;

    const animate = () => {
      if (!scrollContainer || isPaused) return;
      
      scrollPosition += scrollSpeed;
      
      // Reset scroll when reaching the middle section (original testimonials)
      const singleSetWidth = scrollContainer.scrollWidth / 3;
      if (scrollPosition >= singleSetWidth * 2) {
        scrollPosition = singleSetWidth;
        scrollContainer.scrollLeft = scrollPosition;
      }
      
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };

    // Start from middle section
    scrollContainer.scrollLeft = scrollContainer.scrollWidth / 3;
    scrollPosition = scrollContainer.scrollLeft;
    
    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isPaused, testimonials.length]);

  return (
    <section id="video-testimonials" className="pt-8 lg:pt-12 pb-20 lg:pb-32 testimonial-section transition-colors duration-300">
      {/* Section Header */}
      <div className="max-w-[960px] mx-auto px-[7%] sm:px-8 lg:px-8 mb-12">
        <div>
          <div className="mb-4">
            <AnimatedBorderPill text="Testimonials" icon={MessageCircle} />
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold mt-2" style={{ color: headingColor }}>
            Success Stories.
          </h2>
          <p className="mt-2" style={{ color: textColor, fontSize: '14px' }}>
            {description}
          </p>
        </div>
      </div>
      
      {/* Full-width Auto-scrolling Carousel with fade edges */}
      <div className="relative">
        {/* Left fade gradient */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{
            background: `linear-gradient(to right, ${bgColor} 0%, transparent 100%)`,
          }}
        />
        
        {/* Right fade gradient */}
        <div 
          className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{
            background: `linear-gradient(to left, ${bgColor} 0%, transparent 100%)`,
          }}
        />
        
        <div 
          ref={scrollRef}
          className="flex gap-3 overflow-x-hidden cursor-pointer"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          style={{ scrollBehavior: 'auto' }}
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <div
            key={`${testimonial.id}-${index}`}
            className="relative flex-shrink-0 overflow-hidden group cursor-pointer w-[110px] sm:w-[220px]"
            style={{ 
              backgroundColor: cardBgColor,
              border: `1px solid ${borderColor}`, 
              borderRadius: '16px',
              aspectRatio: '9/16',
            }}
            onClick={() => setPlayingVideo(playingVideo === index ? null : index)}
          >
            <div className="w-full h-full">
              {playingVideo === index ? (
                <video
                  src={testimonial.videoUrl}
                  className="w-full h-full object-cover"
                  autoPlay
                  controls
                />
              ) : (
                <>
                  <img
                    src={testimonial.thumbnail}
                    alt={`Testimonial ${testimonial.id}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Play button - smaller on mobile */}
                  <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3">
                    <div 
                      className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/90 hover:bg-white transition-colors"
                      style={{ borderRadius: '50%' }}
                    >
                      <Play className="w-3 h-3 sm:w-4 sm:h-4 ml-0.5" style={{ color: '#111111' }} fill="#111111" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
        </div>
      </div>
    </section>
  );
};

export default EditorialVideoTestimonials;
